import { ObjectiveType } from "@/features/education-management/types/education-management.types";

/** Form'da kullanılan bölüm (chapter) – local state */
export interface FormChapter {
  id: string;
  title: string;
  order: number;
  objectives: FormObjective[];
}

/** Form'da kullanılan hedef (objective) */
export interface FormObjective {
  id: string;
  type: ObjectiveType;
  title: string;
  fileID?: string;
  externalUrl?: string;
  order?: number;
  isVisibleToUser?: boolean;
  videoQuestions?: FormVideoQuestion[];
}

/** Video içi soru – form state */
export interface FormVideoQuestion {
  id: string;
  questionText: string;
  timestamp: string; // "HH:MM:SS"
  options: FormVideoQuestionOption[];
}

export interface FormVideoQuestionOption {
  id: string;
  optionText: string;
  isCorrect: boolean;
}

export const OBJECTIVE_TYPE_LABELS: Record<ObjectiveType, string> = {
  [ObjectiveType.INTERNAL_FILE]: "Video/Dosya",
  [ObjectiveType.LINK]: "Harici link",
  [ObjectiveType.JITSI]: "Jitsi",
};

export const TITLE_MAX_LENGTH = 200;
export const TIMESTAMP_REGEX = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/;

const URL_REGEX = /^https?:\/\/.+/;

/** Bölüm/hedef validasyonu; hata mesajları döner. */
export function validateChapters(chapters: FormChapter[]): string[] {
  const errors: string[] = [];
  chapters.forEach((ch, chIdx) => {
    if (!ch.title.trim()) {
      errors.push(`Bölüm ${chIdx + 1}: Başlık zorunludur.`);
    } else if (ch.title.length > TITLE_MAX_LENGTH) {
      errors.push(`Bölüm ${chIdx + 1}: Başlık en fazla ${TITLE_MAX_LENGTH} karakter olmalıdır.`);
    }
    ch.objectives.forEach((obj, objIdx) => {
      if (!obj.title.trim()) {
        errors.push(`Bölüm ${chIdx + 1}, Hedef ${objIdx + 1}: Başlık zorunludur.`);
      } else if (obj.title.length > TITLE_MAX_LENGTH) {
        errors.push(`Bölüm ${chIdx + 1}, Hedef ${objIdx + 1}: Başlık en fazla ${TITLE_MAX_LENGTH} karakter olmalıdır.`);
      }
      if (obj.type === ObjectiveType.LINK || obj.type === ObjectiveType.JITSI) {
        if (!obj.externalUrl?.trim()) {
          errors.push(`Bölüm ${chIdx + 1}, Hedef ${objIdx + 1}: URL zorunludur.`);
        } else if (!URL_REGEX.test(obj.externalUrl.trim())) {
          errors.push(`Bölüm ${chIdx + 1}, Hedef ${objIdx + 1}: Geçerli bir URL girin (https:// ile başlamalı).`);
        }
      }
      if (obj.videoQuestions?.length) {
        if (obj.type !== ObjectiveType.INTERNAL_FILE || !obj.fileID) {
          errors.push(`Bölüm ${chIdx + 1}, Hedef ${objIdx + 1}: Video soruları için dosya yüklenmiş olmalıdır.`);
        }
        const seenTimestamps = new Set<string>();
        const duplicateTimestamps = new Set<string>();
        obj.videoQuestions.forEach((q, qIdx) => {
          if (!TIMESTAMP_REGEX.test(q.timestamp)) {
            errors.push(`Bölüm ${chIdx + 1}, Hedef ${objIdx + 1}, Soru ${qIdx + 1}: Zaman damgası HH:MM:SS formatında olmalıdır.`);
          } else {
            if (seenTimestamps.has(q.timestamp)) {
              duplicateTimestamps.add(q.timestamp);
            } else {
              seenTimestamps.add(q.timestamp);
            }
          }
          if (q.options.length < 2) {
            errors.push(`Bölüm ${chIdx + 1}, Hedef ${objIdx + 1}, Soru ${qIdx + 1}: En az 2 seçenek girin.`);
          }
          const correctCount = q.options.filter((o) => o.isCorrect).length;
          if (correctCount !== 1) {
            errors.push(`Bölüm ${chIdx + 1}, Hedef ${objIdx + 1}, Soru ${qIdx + 1}: Tam olarak bir doğru cevap işaretleyin.`);
          }
        });
        duplicateTimestamps.forEach((ts) => {
          errors.push(`Bölüm ${chIdx + 1}, Hedef ${objIdx + 1}: Aynı zaman damgasına (${ts}) birden fazla soru eklenemez.`);
        });
      }
    });
  });
  return errors;
}
